import { Link } from 'react-router-dom';
import { ArrowRight, Handshake, Compass, Cog, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { approach } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const stepIcons = [Compass, Handshake, Cog, Activity];

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

export default function ApproachPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            How We Work
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Our Approach
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            A structured, partnership-driven methodology that ensures every engagement delivers
            measurable value aligned with your strategic priorities.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <Reveal>
              <div>
                <h2 className="text-2xl font-light text-[#0B1D33] mb-6 tracking-tight">
                  Partnership, Not Prescription
                </h2>
                <div className="space-y-5 text-slate-500 leading-relaxed">
                  <p>
                    We believe effective cybersecurity advisory requires deep understanding of your
                    business context, not templated solutions. Our approach begins with listening, understanding
                    your organization's unique risk landscape, regulatory obligations, strategic objectives,
                    and operational constraints.
                  </p>
                  <p>
                    From that foundation of understanding, we design and deliver solutions that are
                    tailored, actionable, and sustainable. We work alongside your teams, not above them,
                    ensuring knowledge transfer and capability building at every stage.
                  </p>
                  <p>
                    The result is not just a deliverable. It's a partnership that strengthens your
                    organization's security posture and strategic decision-making capacity for the long term.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="bg-[#F8FAFC] rounded-lg border border-slate-200/80 p-8 lg:p-10">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
                  Our Principles
                </h3>
                <ul className="space-y-6">
                  {[
                    { title: 'Executive Alignment', desc: 'Every recommendation is framed for leadership decision-making and board-level communication.' },
                    { title: 'Outcome Orientation', desc: 'We measure success by measurable risk reduction and business impact, not activity volume.' },
                    { title: 'Knowledge Transfer', desc: 'We build lasting capability within your organization, not dependency on external advisors.' },
                    { title: 'Disciplined Execution', desc: 'Structured methodologies ensure consistency, quality, and predictability across every engagement.' },
                  ].map((item, i) => (
                    <li key={i}>
                      <h4 className="text-sm font-semibold text-[#0B1D33] mb-1.5">{item.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Methodology Steps */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-16">
              <p className="text-[#3B82A0] text-xs font-semibold uppercase tracking-[0.2em] mb-3">
                Methodology
              </p>
              <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-5 tracking-tight">
                Strategy → Design → Implement → Operate
              </h2>
              <p className="text-slate-500 leading-relaxed">
                Our four-phase engagement model provides a clear, repeatable framework for delivering
                security outcomes at the pace your organization requires.
              </p>
            </div>
          </Reveal>

          <div className="space-y-6">
            {approach.map((step, i) => {
              const Icon = stepIcons[i];
              return (
                <Reveal key={step.step} delay={i * 100}>
                  <div className="bg-white rounded-lg border border-slate-200/80 p-8 lg:p-10 hover:shadow-md transition-shadow duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                      <div className="flex items-center gap-5 lg:w-64 flex-shrink-0">
                        <div className="w-12 h-12 rounded-lg bg-[#3B82A0]/10 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-[#3B82A0]" strokeWidth={1.5} />
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            Phase {step.step}
                          </span>
                          <h3 className="text-xl font-semibold text-[#0B1D33]">{step.title}</h3>
                        </div>
                      </div>
                      <div className="lg:flex-1">
                        <p className="text-slate-500 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-5 tracking-tight">
              Begin the Conversation
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto mb-8 leading-relaxed">
              Let us understand your challenges and demonstrate how our structured approach delivers
              the outcomes your organization needs.
            </p>
            <Link to="/contact">
              <Button className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200">
                Request a Consultation
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
